import type { ScheduleEntry } from '@dosfilos/domain';

// ─── El editor de horario de una sección: lo que no es pantalla (M1·H7b) ────
// Observación de Ricardo (2026-10-16): al agregar un bloque, el inicio quedó en
// 1:00 AM. El bloque nuevo empezaba vacío, pero la lista de horas empezaba a
// medianoche: al abrirla el foco caía en «12:00 AM», y un Enter, un clic de más
// o un desplazamiento corto dejaban una hora de madrugada (llegar a las 10:00
// AM eran 40 opciones). Ahora:
//  - la lista empieza a las 06:00 y la madrugada queda al final;
//  - un bloque nuevo copia las horas del anterior (vacío si es el primero);
//  - el editor AVISA, sin bloquear, si un bloque empieza entre las 00:00 y las
//    06:00 o dura más de 6 horas.

/** Cada 15 minutos, desde las 06:00; de 00:00 a 05:45 al final. */
export const TIME_OPTIONS: readonly string[] = (() => {
  const all = Array.from(
    { length: 96 },
    (_, i) =>
      `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`,
  );
  return [...all.slice(24), ...all.slice(0, 24)];
})();

/** Las opciones para un valor dado: si es una hora fuera de la grilla (19:10),
 *  se agrega en su lugar para no perderla al abrir el editor. */
export function timeOptionsFor(current: string): readonly string[] {
  if (!current || TIME_OPTIONS.includes(current)) return TIME_OPTIONS;
  const morningFirst = (t: string) => (t < '06:00' ? `1${t}` : `0${t}`);
  return [...TIME_OPTIONS, current].sort((a, b) => morningFirst(a).localeCompare(morningFirst(b)));
}

/** El bloque que se agrega: el día siguiente al del anterior, con sus mismas
 *  horas y su sala; el primero, vacío (lunes, sin horas). */
export function nextBlock(previous: ScheduleEntry | undefined): ScheduleEntry {
  if (!previous) return { dayOfWeek: 1, startTime: '', endTime: '' };
  return {
    dayOfWeek: (previous.dayOfWeek + 1) % 7,
    startTime: previous.startTime,
    endTime: previous.endTime,
    ...(previous.location ? { location: previous.location } : {}),
  };
}

export type BlockWarning = 'early_start' | 'long_block';

const minutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
};

/** Avisos (no errores) de un bloque: empieza de madrugada, o dura más de 6 h. */
export function blockWarnings(b: { startTime: string; endTime: string }): BlockWarning[] {
  const out: BlockWarning[] = [];
  const valid = (t: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
  if (valid(b.startTime) && b.startTime < '06:00') out.push('early_start');
  if (
    valid(b.startTime) &&
    valid(b.endTime) &&
    minutes(b.endTime) - minutes(b.startTime) > 6 * 60
  ) {
    out.push('long_block');
  }
  return out;
}
